import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AmountDiscountResponse,
  ApplicationControllerApi,
  ApplicationResponse,
  FeePaymentRequest,
  MoneyValue,
  MoneyValueCurrencyEnum,
  TeamPublicControllerApi,
  TeamInvitationDto,
  ValidateGenericTeamInvitationResponse,
} from '../openapi/yenta';
import {
  PAYMENT_CONSTANTS,
  convertFromStripeCents,
  convertToStripeCents,
} from '../components/payment/constants';
import ErrorService from '../services/ErrorService';
import { AppThunk, InvitationType } from '../types';
import Logger from '../utils/Logger';
import { getYentaConfiguration } from '../utils/OpenapiConfigurationUtils';
import { showErrorToast, showSuccessToast } from './ToastNotificationSlice';

interface PaymentState {
  promoDiscountInfo: AmountDiscountResponse | null;
  isApplyingPromo: boolean;
  isProcessingPayment: boolean;
  lastPaymentResponse: ApplicationResponse | null;
  invitationCouponCode: string | null;
  isInvitationDiscount: boolean;
  invitationId: string | null;
  invitationType: InvitationType | null;
}

export const initialState: PaymentState = {
  promoDiscountInfo: null,
  isApplyingPromo: false,
  isProcessingPayment: false,
  lastPaymentResponse: null,
  invitationCouponCode: null,
  isInvitationDiscount: false,
  invitationId: null,
  invitationType: null,
};

const PaymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    savePromoDiscountInfo(
      state,
      action: PayloadAction<AmountDiscountResponse | null>,
    ) {
      state.promoDiscountInfo = action.payload;
    },
    setApplyingPromo(state, action: PayloadAction<boolean>) {
      state.isApplyingPromo = action.payload;
    },
    setProcessingPayment(state, action: PayloadAction<boolean>) {
      state.isProcessingPayment = action.payload;
    },
    saveLastPaymentResponse(
      state,
      action: PayloadAction<ApplicationResponse | null>,
    ) {
      state.lastPaymentResponse = action.payload;
    },
    clearPromoCode(state) {
      if (!state.isInvitationDiscount) {
        state.promoDiscountInfo = null;
      }
    },
    setInvitationDetails(
      state,
      action: PayloadAction<{
        invitationId: string | null;
        invitationType: InvitationType | null;
        couponCode: string | null;
      }>,
    ) {
      state.invitationId = action.payload.invitationId;
      state.invitationType = action.payload.invitationType;
      state.invitationCouponCode = action.payload.couponCode;
    },
    setIsInvitationDiscount(state, action: PayloadAction<boolean>) {
      state.isInvitationDiscount = action.payload;
    },
  },
});

export const {
  savePromoDiscountInfo,
  setApplyingPromo,
  setProcessingPayment,
  saveLastPaymentResponse,
  clearPromoCode,
  setInvitationDetails,
  setIsInvitationDiscount,
} = PaymentSlice.actions;

export { InvitationType };

export const applyPromoCode =
  (
    applicationId: string,
    couponCode: string,
    amount: number,
  ): AppThunk<Promise<AmountDiscountResponse | null>> =>
  async (dispatch) => {
    dispatch(setApplyingPromo(true));

    try {
      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).calculateAmountDiscountInfo(
        applicationId,
        couponCode,
        convertToStripeCents(amount),
      );

      const discountInfo: AmountDiscountResponse = {
        ...data,
        calculatedDiscount: convertFromStripeCents(
          data.calculatedDiscount || 0,
        ),
        originalAmount: convertFromStripeCents(data.originalAmount || 0),
      };

      dispatch(savePromoDiscountInfo(discountInfo));
      dispatch(showSuccessToast('Promo code applied successfully'));

      return discountInfo;
    } catch (e) {
      Logger.error('Failed to apply promo code:', e);
      ErrorService.notifyIgnoreAuthErrors(
        'Failed to apply promo code',
        e as Error,
      );

      const errorMessage =
        (e as any)?.response?.data?.message || 'Invalid promo code';
      dispatch(showErrorToast(errorMessage));

      return null;
    } finally {
      dispatch(setApplyingPromo(false));
    }
  };

export const processPayment =
  (
    applicationId: string,
    paymentMethodId: string = '', // Empty string for skip payment
    totalAmount: number,
    promoCode?: string,
    currency: MoneyValueCurrencyEnum = MoneyValueCurrencyEnum.Usd,
  ): AppThunk<Promise<ApplicationResponse | null>> =>
  async (dispatch) => {
    dispatch(setProcessingPayment(true));

    const isSkipPayment =
      !paymentMethodId || totalAmount < PAYMENT_CONSTANTS.MIN_PAYMENT_AMOUNT;

    try {
      const paymentRequest: FeePaymentRequest = {
        applicationId,
        fees: {
          amount: convertToStripeCents(totalAmount),
          currency,
        } as MoneyValue,
        paymentMethod: paymentMethodId || '', // Empty string if no payment method
        promoCode: promoCode || '',
      };

      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).payApplicationFees(paymentRequest);

      dispatch(saveLastPaymentResponse(data));

      return data;
    } catch (e) {
      const actionType = isSkipPayment
        ? 'submit application'
        : 'process payment';
      Logger.error(`Unable to ${actionType}:`, e);
      ErrorService.notifyIgnoreAuthErrors(
        `Unable to ${actionType}`,
        e as Error,
      );

      const errorMessage =
        (e as any)?.response?.data?.message ||
        `Unable to ${actionType}. Please try again later.`;
      dispatch(showErrorToast(errorMessage));

      return null;
    } finally {
      dispatch(setProcessingPayment(false));
    }
  };

export const checkAndApplyInvitationCoupon =
  (
    applicationId: string,
    invitationId: string | null,
    invitationType: InvitationType | null,
    amount: number,
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    if (!invitationId || !invitationType || !applicationId || amount <= 0) {
      return;
    }

    dispatch(setApplyingPromo(true));

    try {
      const config = await getYentaConfiguration();
      const publicApi = new TeamPublicControllerApi(config);
      let invitation:
        | TeamInvitationDto
        | ValidateGenericTeamInvitationResponse
        | null = null;
      let couponCode: string | undefined;

      if (invitationType === InvitationType.EMAIL) {
        const { data } = await publicApi.getTeamInvitation(invitationId);
        invitation = data;
        couponCode = data.couponCode;
      } else if (invitationType === InvitationType.LINK) {
        const { data } =
          await publicApi.validateGenericInvitation(invitationId);
        invitation = data;
        couponCode = data.invitation?.couponCode;
      }

      if (!invitation || !couponCode) {
        Logger.log('No coupon code found in invitation');
        return;
      }

      dispatch(
        setInvitationDetails({
          invitationId,
          invitationType,
          couponCode,
        }),
      );

      const api = new ApplicationControllerApi(config);
      const { data } = await api.calculateAmountDiscountInfo(
        applicationId,
        couponCode,
        convertToStripeCents(amount),
      );

      const discountInfo: AmountDiscountResponse = {
        ...data,
        calculatedDiscount: convertFromStripeCents(
          data.calculatedDiscount || 0,
        ),
        originalAmount: convertFromStripeCents(data.originalAmount || 0),
      };

      dispatch(savePromoDiscountInfo(discountInfo));
      dispatch(setIsInvitationDiscount(true));

      if (
        discountInfo.calculatedDiscount &&
        discountInfo.calculatedDiscount > 0
      ) {
        dispatch(
          showSuccessToast('Application fees waived through invitation'),
        );
      }
    } catch (e) {
      Logger.error('Unable to apply invitation coupon:', e);
    } finally {
      dispatch(setApplyingPromo(false));
    }
  };

export default PaymentSlice.reducer;
